// Shared helper that computes the blended (manual + FE) portfolio sector
// matrix for P / R / Dist dashboards and the adviser-presentation positioning.
//
// Per fund the source is decided by sectorSourceFor() — manual mapping when the fund has one,
// FE's breakdown only as a fallback for a fund that has none. That rule is shared with
// src/components/benchmark/BlendedSectorWeightings.tsx so every screen agrees on a single
// P-side number; it used to be a 50/50 average duplicated as a bare literal in both files.

import { sectorSourceFor } from "@/lib/sectorSourcePolicy";
import {
  resolveFundCbam,
  type FeBreakdownRow,
  type FeMappingRow,
  type FeSplitOverride,
} from "@/components/fe/feMatching";

export interface BlendedFundWeight {
  fund_id: string;
  portfolio: number;
  weight_pct: number;
}
export interface BlendedSectorMapping {
  fund_id: string;
  sector_id: string;
  weighting: number;
}
export interface BlendedSector {
  id: string;
  name: string;
}
export interface BlendedCbamFund {
  id: string;
  isin: string | null;
}

export interface BuildBlendedMatrixInput {
  weights: BlendedFundWeight[];
  mappings: BlendedSectorMapping[];
  sectors: BlendedSector[];
  cbamFunds: BlendedCbamFund[];
  feRows: FeBreakdownRow[];
  feMapping: FeMappingRow[];
  feOverrides: FeSplitOverride[];
}

/** Returns a map keyed `${portfolio}-${sector_id}` -> blended weight in %. */
export function buildBlendedPortfolioMatrix(
  input: BuildBlendedMatrixInput,
): Map<string, number> {
  const matrix = new Map<string, number>();

  const heldFundIds = new Set(
    input.weights.filter((w) => Number(w.weight_pct) > 0).map((w) => w.fund_id),
  );
  const sectorIdByName = new Map(input.sectors.map((s) => [s.name, s.id]));

  const mappingsByFund = new Map<string, BlendedSectorMapping[]>();
  for (const m of input.mappings) {
    if (!mappingsByFund.has(m.fund_id)) mappingsByFund.set(m.fund_id, []);
    mappingsByFund.get(m.fund_id)!.push(m);
  }

  const mappingByCol = new Map(input.feMapping.map((m) => [m.fe_column_name, m]));
  const overridesByIsinCol = new Map<string, FeSplitOverride[]>();
  for (const o of input.feOverrides) {
    const k = `${o.isin}::${o.fe_column_name}`;
    if (!overridesByIsinCol.has(k)) overridesByIsinCol.set(k, []);
    overridesByIsinCol.get(k)!.push(o);
  }

  const isinByFundId = new Map(
    input.cbamFunds.map((f) => [f.id, (f.isin ?? "").toUpperCase().trim()]),
  );
  const feByIsin = new Map<string, FeBreakdownRow>();
  for (const r of input.feRows) {
    if (r.isin) feByIsin.set(r.isin.toUpperCase().trim(), r);
  }

  for (const w of input.weights) {
    if (!heldFundIds.has(w.fund_id) || Number(w.weight_pct) <= 0) continue;

    const fundMappings = mappingsByFund.get(w.fund_id) ?? [];
    const hasManual = fundMappings.length > 0;

    let feAlloc: Record<string, number> | null = null;
    const isin = isinByFundId.get(w.fund_id);
    if (isin) {
      const fe = feByIsin.get(isin);
      if (fe) {
        const { allocation, total } = resolveFundCbam(
          fe,
          mappingByCol,
          overridesByIsinCol,
        );
        const hasUnresolved = Object.keys(allocation).some(
          (k) => k.startsWith("[needs split] ") || k === "Unknown",
        );
        if (total > 0 && !hasUnresolved && Math.abs(total - 100) < 1) {
          feAlloc = allocation;
        }
      }
    }
    const hasFe = feAlloc !== null;

    // Manual first, FE as backup. Policy and rationale live in one place so the two
    // implementations of this arithmetic cannot drift apart — see sectorSourcePolicy.ts.
    const { manualShare, feShare } = sectorSourceFor(hasManual, hasFe);
    if (manualShare === 0 && feShare === 0) continue;

    if (manualShare > 0) {
      for (const m of fundMappings) {
        const key = `${w.portfolio}-${m.sector_id}`;
        const contribution =
          Number(w.weight_pct) * Number(m.weighting) * manualShare;
        matrix.set(key, (matrix.get(key) || 0) + contribution);
      }
    }

    if (feShare > 0 && feAlloc) {
      for (const [sectorName, pct] of Object.entries(feAlloc)) {
        const sectorId = sectorIdByName.get(sectorName);
        if (!sectorId) continue;
        const key = `${w.portfolio}-${sectorId}`;
        const contribution =
          Number(w.weight_pct) * (Number(pct) / 100) * feShare;
        matrix.set(key, (matrix.get(key) || 0) + contribution);
      }
    }
  }

  return matrix;
}

/** Convenience: fetch all source data and build the matrix in one call. */
export async function fetchAndBuildBlendedMatrix(
  supabase: any,
  weightsTable:
    | "ba_cbam_fund_weights"
    | "ba_r_cbam_fund_weights"
    | "ba_dist_cbam_fund_weights",
): Promise<Map<string, number>> {
  const [weightsRes, mappingsRes, sectorsRes, fundsRes, feRowsRes, feMapRes, feOvRes] =
    await Promise.all([
      supabase
        .from(weightsTable)
        .select("fund_id, portfolio, weight_pct, effective_date")
        .order("effective_date", { ascending: false }),
      supabase
        .from("ba_cbam_fund_sector_mappings")
        .select("fund_id, sector_id, weighting"),
      supabase.from("ba_master_sectors").select("id, name"),
      supabase.from("ba_cbam_funds").select("id, isin"),
      supabase
        .from("fe_asset_breakdown")
        .select("id, isin, fund_name, as_of_date, breakdown")
        .order("as_of_date", { ascending: false }),
      supabase.from("fe_sector_mapping").select("*").order("display_order"),
      supabase.from("fe_fund_split_overrides").select("*"),
    ]);

  // Latest weight per (fund_id, portfolio).
  const seen = new Set<string>();
  const latestWeights: BlendedFundWeight[] = [];
  for (const w of (weightsRes.data ?? []) as any[]) {
    const key = `${w.fund_id}::${w.portfolio}`;
    if (seen.has(key)) continue;
    seen.add(key);
    latestWeights.push({
      fund_id: w.fund_id,
      portfolio: w.portfolio,
      weight_pct: Number(w.weight_pct),
    });
  }

  // Latest FE breakdown per ISIN.
  const latestFe = new Map<string, FeBreakdownRow>();
  for (const r of (feRowsRes.data ?? []) as any[]) {
    if (!r.isin) continue;
    if (!latestFe.has(r.isin)) {
      latestFe.set(r.isin, { ...r, breakdown: r.breakdown ?? {} });
    }
  }

  return buildBlendedPortfolioMatrix({
    weights: latestWeights,
    mappings: (mappingsRes.data ?? []) as BlendedSectorMapping[],
    sectors: (sectorsRes.data ?? []) as BlendedSector[],
    cbamFunds: (fundsRes.data ?? []) as BlendedCbamFund[],
    feRows: Array.from(latestFe.values()),
    feMapping: (feMapRes.data ?? []) as FeMappingRow[],
    feOverrides: (feOvRes.data ?? []) as FeSplitOverride[],
  });
}
